import * as dateFns from 'date-fns'
import React from 'react'

import { Button } from '../../../main/components/button'
import { DialogBody, DialogFooter } from '../../../main/components/dialog'
import { Paragraph } from '../../../main/components/typography'
import { CostPerShareEditColumn } from '../../../main/modules/portfolio/components/EditableColumns/CostPerShare'
import { DateEditColumn } from '../../../main/modules/portfolio/components/EditableColumns/Date'
import { SharesEditColumn } from '../../../main/modules/portfolio/components/EditableColumns/Shares'
import { TickerEditColumn } from '../../../main/modules/portfolio/components/EditableColumns/Ticker'
import { TransactionEditColumn } from '../../../main/modules/portfolio/components/EditableColumns/Transaction'
import { PortfolioBulkEditRowContextProvider } from '../../../main/modules/portfolio/components/PortfolioBulkEditRowContext'
import { PORTFOLIO_DATE_FORMAT_DATA, TRANSACTION_LABEL } from '../../../main/modules/portfolio/constants/general'
import { PortfolioTransaction } from '../../../main/modules/portfolio/types'
import { lastCloseRequest, portfoliosRequest } from '../../../main/services/api'
import PortfolioInput from './PortfolioInput'
import { FormErrors, PortfolioOptions, SaveToPortfolioEntry } from './types'
import { submitForm, validateState } from './utils'

const DEFAULT_STATE: SaveToPortfolioEntry = {
  portfolioId: 0,
  portfolioName: '',
  ticker: '',
  transaction: PortfolioTransaction.Watch,
  date: dateFns.format(new Date(), PORTFOLIO_DATE_FORMAT_DATA),
  shares: 0,
  price: 0,
}

const TRANSACTION_OPTIONS = Object.entries(TRANSACTION_LABEL).map(([value, label]) => ({
  value: Number(value),
  label,
}))

type FormContentProps = {
  ticker: string
  onSubmit: (portfolioId: number) => void
  onClose?: () => void
}

export default function FormContent({ ticker, onClose, onSubmit }: FormContentProps) {
  const [state, setState] = React.useState({ ...DEFAULT_STATE, ticker })
  const [errors, setErrors] = React.useState<FormErrors>({})
  const [portfolios, setPortfolios] = React.useState<PortfolioOptions>([])
  const [apiError, setApiError] = React.useState<string>()
  const formRef = React.useRef<HTMLFormElement>(null)

  // fetch portfolios and stock data on mount
  React.useEffect(() => {
    const fetchPortfolios = async () => {
      const response = await portfoliosRequest()
      if (response && response.portfolios.length > 0) {
        setState((prev) => ({ ...prev, portfolioId: response.portfolios[0].id }))
        setPortfolios(response.portfolios.map((p) => ({ value: p.id, label: p.name })))
      }
    }
    const fetchLastClose = async () => {
      const lastClose = await lastCloseRequest(ticker)
      if (lastClose) {
        setState((prev) => ({ ...prev, price: +lastClose }))
      }
    }

    try {
      fetchPortfolios()
      if (!!ticker) {
        fetchLastClose()
      }
    } catch (error) {}

    // this should be run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const { isValid, errors } = await validateState(state)
    setErrors(errors)

    if (!isValid) {
      setTimeout(() => {
        formRef.current?.querySelector<HTMLInputElement>('.is-error input')?.focus?.()
      })
      return
    }

    const entry = {
      ...state,
      ...(state.transaction === PortfolioTransaction.Watch && {
        date: DEFAULT_STATE.date,
        shares: DEFAULT_STATE.shares,
        price: DEFAULT_STATE.price,
      }),
    }

    const result = await submitForm(entry)
    if (!result.isSuccess) {
      setApiError(result.message)
    } else {
      onSubmit(state.portfolioId)
    }
  }

  const commonProps = {
    entry: state,
    errors,
    setState,
    setErrors,
    isHorizontalLabel: true,
    className: 'w-56',
    labelClassName: 'flex-row items-center justify-between',
    size: 'small' as const,
  }

  return (
    <PortfolioBulkEditRowContextProvider hasAlwaysActiveOverride>
      <form ref={formRef} noValidate autoComplete="off" onSubmit={handleSubmit}>
        <DialogBody className="flex flex-col space-y-2">
          {(errors.portfolioId || apiError) && (
            <Paragraph size="small" className="mb-4 text-center text-negative">
              {apiError ?? 'Portfolio limit exceeded, select another portfolio.'}
            </Paragraph>
          )}
          <PortfolioInput
            portfolios={portfolios}
            state={state}
            setState={setState}
            errors={errors}
            setErrors={setErrors}
          />
          <TickerEditColumn {...commonProps} label="Ticker" />
          <TransactionEditColumn
            {...commonProps}
            label="Transaction"
            optionListProps={{ className: 'min-w-56' }}
            transactions={TRANSACTION_OPTIONS}
          />
          {state.transaction !== PortfolioTransaction.Watch && (
            <>
              <DateEditColumn {...commonProps} label="Date" />
              <SharesEditColumn {...commonProps} label="Shares" inputClassName="text-left" />
              <CostPerShareEditColumn {...commonProps} label="Cost / Share" inputClassName="text-left" />
            </>
          )}
        </DialogBody>
        <DialogFooter>
          {onClose && (
            <Button type="button" onClick={onClose}>
              Close
            </Button>
          )}
          <Button type="submit" theme="blue">
            Save
          </Button>
        </DialogFooter>
      </form>
    </PortfolioBulkEditRowContextProvider>
  )
}
